/**
 * Before / after image comparison.
 * Drag (mouse, touch, pen), click anywhere on the image, or use the arrow keys on the handle.
 * The position is written to --vb-pos on the element; CSS clips the two layers from it.
 */
class ImageComparisonSlider extends HTMLElement {
  connectedCallback() {
    this.handle = this.querySelector('.landing-visual-benefit__handle');
    if (!this.handle) return;

    // Each photo covers 773 of the 828px frame, so stop where a photo would run out
    this.min = (55 / 828) * 100;
    this.max = 100 - this.min;
    this.position = 50;
    this.handle.setAttribute('aria-valuemin', Math.round(this.min));
    this.handle.setAttribute('aria-valuemax', Math.round(this.max));

    this.onPointerDown = (e) => {
      if (e.button !== undefined && e.button !== 0) return;
      this.dragging = true;
      this.setPointerCapture?.(e.pointerId);
      this.classList.add('is-dragging');
      this.setFromPointer(e);
    };
    this.onPointerMove = (e) => {
      if (this.dragging) this.setFromPointer(e);
    };
    this.onPointerUp = (e) => {
      if (!this.dragging) return;
      this.dragging = false;
      this.releasePointerCapture?.(e.pointerId);
      this.classList.remove('is-dragging');
    };
    this.onKeydown = (e) => {
      const step = e.shiftKey ? 10 : 2;
      const keys = {
        ArrowLeft: this.position - step,
        ArrowDown: this.position - step,
        ArrowRight: this.position + step,
        ArrowUp: this.position + step,
        Home: this.min,
        End: this.max,
      };
      if (!(e.key in keys)) return;
      e.preventDefault();
      this.setPosition(keys[e.key]);
    };

    this.addEventListener('pointerdown', this.onPointerDown);
    this.addEventListener('pointermove', this.onPointerMove);
    this.addEventListener('pointerup', this.onPointerUp);
    this.addEventListener('pointercancel', this.onPointerUp);
    this.handle.addEventListener('keydown', this.onKeydown);
    // Stop the browser's native image drag from hijacking the gesture
    this.addEventListener('dragstart', (e) => e.preventDefault());

    this.setPosition(50);
  }

  setFromPointer(e) {
    const rect = this.getBoundingClientRect();
    if (!rect.width) return;
    this.setPosition(((e.clientX - rect.left) / rect.width) * 100);
  }

  setPosition(percent) {
    this.position = Math.min(this.max, Math.max(this.min, percent));
    this.style.setProperty('--vb-pos', `${this.position}%`);

    const rounded = Math.round(this.position);
    this.handle.setAttribute('aria-valuenow', rounded);
    this.handle.setAttribute('aria-valuetext', `${rounded}%`);
  }
}

if (!customElements.get('image-comparison-slider')) {
  customElements.define('image-comparison-slider', ImageComparisonSlider);
}
